import React, { useEffect, useState, useRef } from 'react';
import { Container, Icon, Button } from '@socialchorus/shared-ui-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { useAISummaryStreaming } from '../../../../common/streaming/use-ai-summary-streaming';
import { MessageUpdateEvent } from '../../../../models/chatbot/types';
import { chatbotSessionState } from '../../../../models/chatbot/recoil-state';
import ChatbotMarkdown from '../../../chatbot/ChatbotMarkdown';
import styles from './ai-summary.module.scss';
import { LoadingSkeleton } from '../../loading-skeleton';
import useFeatureFlag from '../../../../common/use-feature-flag';
import { Feature } from '../../../../models/features/features';

interface AISummaryProps {
  query: string;
  programId: string;
}

const AISummary: React.FC<AISummaryProps> = ({ query, programId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const setChatbotSession = useSetRecoilState(chatbotSessionState);
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const performSearch = useAISummaryStreaming(programId);
  const chatbotEnabled = useFeatureFlag(Feature.CHATBOT_ENABLED);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Check for overflow when content changes (including during streaming)
  useEffect(() => {
    if (contentRef.current) {
      const element = contentRef.current;
      const isOverflowing = element.scrollHeight > 200; // 200px is our max-height
      setHasOverflow(isOverflowing);
    }
  }, [content]);

  const handleDeepDive = () => {
    if (threadId) {
      setChatbotSession({
        threadId,
        isThreadPersisted: true,
      });
      history.push('/assistant');
    }
  };

  useEffect(() => {
    if (query && programId) {
      // Reset state for new search
      setContent('');
      setIsLoading(true);
      setThreadId(undefined);
      setIsExpanded(false);
      setHasOverflow(false);

      performSearch(query, (event) => {
        switch (event.type) {
          case 'stream_start':
            // Capture thread ID for deep dive
            setThreadId(event.data.threadId);
            break;

          case 'message_update':
            // Append text chunks to content
            setContent(
              (prev) => prev + (event as MessageUpdateEvent).data.chunk
            );
            break;

          case 'stream_end':
            // Stream completed
            setIsLoading(false);
            break;

          case 'error':
            // Stream error - stop loading
            setIsLoading(false);
            break;

          case 'message_reference':
          case 'task_started':
          case 'task_completed':
          case 'ping':
          case 'placeholder_update':
          case 'results_available':
          case 'no_results':
            // These events don't need special handling for AI summary
            break;
        }
      });
    }
  }, [query, programId, performSearch]);

  // Don't show anything if no query
  if (!query) {
    return null;
  }

  return (
    <Container
      shadow="none"
      shape="large"
      variant="default"
      className={`${styles.AISummaryContainer} ${
        isLoading ? styles.Loading : ''
      }`}
      role="region"
      aria-label={t('search.ai_summary.title')}
      aria-busy={isLoading}
    >
      <div className={styles.Header}>
        <div className={styles.TitleContainer}>
          <h2 className={styles.Title}>{t('search.ai_summary.title')}</h2>
        </div>

        {isLoading && content && hasOverflow && (
          <span
            className={styles.TypingIndicator}
            role="status"
            aria-label={t('search.ai_summary.generating')}
            data-testid="playwright-ai-summary-streaming-indicator"
          >
            <span className={styles.Dot}></span>
            <span className={styles.Dot}></span>
            <span className={styles.Dot}></span>
          </span>
        )}

        {!isLoading && threadId && chatbotEnabled && (
          <Button
            variant="outline"
            size="compact"
            onClick={handleDeepDive}
            label={t('search.ai_summary.search_with_assistant')}
            leftIcon={
              <Icon className={styles.AssistantButtonIcon} filled>
                auto_awesome
              </Icon>
            }
            className={styles.SearchWithAssistantButton}
          />
        )}
      </div>

      {isLoading && !content ? (
        <div className={styles.LoadingContent}>
          <LoadingSkeleton width="100%" height="12px" marginTop="0" />
          <LoadingSkeleton width="95%" height="12px" marginTop="8px" />
          <LoadingSkeleton width="90%" height="12px" marginTop="8px" />
        </div>
      ) : (
        <>
          <div
            ref={contentRef}
            className={`${styles.Content} ${
              !isExpanded && hasOverflow ? styles.Collapsed : ''
            } ${isLoading ? styles.Loading : ''}`}
            data-testid="playwright-ai-summary-content"
          >
            <ChatbotMarkdown content={content} />
            {isLoading && !hasOverflow && (
              <span
                className={styles.TypingIndicator}
                role="status"
                aria-label={t('search.ai_summary.generating')}
                data-testid="playwright-ai-summary-streaming-indicator"
              >
                <span className={styles.Dot}></span>
                <span className={styles.Dot}></span>
                <span className={styles.Dot}></span>
              </span>
            )}
          </div>
          {hasOverflow && (
            <div className={styles.SeeMoreContainer}>
              <Button
                variant="text"
                size="compact"
                onClick={toggleExpanded}
                rightIcon={<Icon>expand_more</Icon>}
                fullWidth={true}
                label={
                  isExpanded
                    ? t('search.ai_summary.see_less')
                    : t('search.ai_summary.see_more')
                }
                className={styles.SeeMoreButton}
              />
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default AISummary;
